/**
 * Weather Dashboard Page
 *
 * Comprehensive weather page with current conditions, 7-day forecast,
 * rainfall analysis, growing degree days, frost/heat alerts, and
 * agricultural weather advisories.
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  SunIcon,
  CloudIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { farmApi, weatherApi } from '../lib/api';
import { Card, CardHeader, CardTitle } from '../components/ui';

// ============ WEATHER CODE HELPERS ============

function getWeatherIcon(condition: string) {
  const lower = (condition || '').toLowerCase();
  if (lower.includes('clear') || lower.includes('sunny')) return SunIcon;
  return CloudIcon;
}

function getWeatherGradient(condition: string): string {
  const lower = (condition || '').toLowerCase();
  if (lower.includes('clear') || lower.includes('sunny'))
    return 'from-amber-400 via-orange-400 to-yellow-300';
  if (lower.includes('partly'))
    return 'from-sky-400 via-blue-400 to-sky-300';
  if (lower.includes('cloud') || lower.includes('overcast'))
    return 'from-gray-400 via-gray-500 to-slate-400';
  if (lower.includes('rain') || lower.includes('drizzle'))
    return 'from-blue-500 via-blue-600 to-indigo-500';
  if (lower.includes('thunder') || lower.includes('storm'))
    return 'from-purple-600 via-indigo-700 to-gray-800';
  if (lower.includes('fog'))
    return 'from-gray-300 via-gray-400 to-slate-300';
  if (lower.includes('snow'))
    return 'from-blue-100 via-blue-200 to-white';
  return 'from-sky-500 via-blue-500 to-indigo-500';
}

// ============ TAB TYPES ============

type TabId = 'current' | 'forecast' | 'rainfall' | 'gdd' | 'alerts' | 'advisory';

const TABS: { id: TabId; label: string }[] = [
  { id: 'current', label: 'Current' },
  { id: 'forecast', label: '7-Day Forecast' },
  { id: 'rainfall', label: 'Rainfall' },
  { id: 'gdd', label: 'GDD' },
  { id: 'alerts', label: 'Frost & Heat' },
  { id: 'advisory', label: 'Advisory' },
];

// ============ CUSTOM TOOLTIP ============

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number; name: string; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg px-3 py-2 shadow-lg border border-gray-100 text-sm">
      <p className="font-medium text-gray-600 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
        </p>
      ))}
    </div>
  );
}

// ============ MAIN COMPONENT ============

export default function Weather() {
  const [selectedFarmId, setSelectedFarmId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabId>('current');
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [gddBaseTemp, setGddBaseTemp] = useState(10);

  // Fetch farms
  const { data: farms = [] } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  // Auto-select first farm
  const farmId = selectedFarmId || farms[0]?.id || '';
  const selectedFarm = farms.find((f) => f.id === farmId);

  // Fetch current weather
  const { data: currentData, isLoading: loadingCurrent } = useQuery({
    queryKey: ['weather-current', farmId],
    queryFn: () => weatherApi.getCurrent(farmId),
    enabled: !!farmId,
  });

  // Fetch 7-day forecast
  const { data: forecastData, isLoading: loadingForecast } = useQuery({
    queryKey: ['weather-forecast', farmId],
    queryFn: () => weatherApi.getForecast(farmId, 7),
    enabled: !!farmId,
  });

  // Fetch 30-day historical
  const { data: historicalData, isLoading: loadingHistorical } = useQuery({
    queryKey: ['weather-historical', farmId],
    queryFn: () => {
      const end = new Date().toISOString().split('T')[0];
      const start = subDays(new Date(), 30).toISOString().split('T')[0];
      return weatherApi.getHistory(farmId, start, end);
    },
    enabled: !!farmId,
  });

  // Fetch GDD (90-day or from planting date)
  const { data: gddData } = useQuery({
    queryKey: ['weather-gdd', farmId, gddBaseTemp],
    queryFn: async () => {
      const end = new Date().toISOString().split('T')[0];
      const start = selectedFarm?.plantingDate || subDays(new Date(), 90).toISOString().split('T')[0];
      const resp = await weatherApi.getHistory(farmId, start, end);
      return resp;
    },
    enabled: !!farmId,
  });

  // ============ DERIVED DATA ============

  const forecast = forecastData?.forecast || [];
  const historical = historicalData?.data || [];
  const alerts = forecastData?.alerts || [];

  // Rainfall chart data
  const rainfallData = useMemo(() => {
    if (!historical.length) return [];
    let cumulative = 0;
    return historical.map((d: { date: string; precipitation: number }) => {
      cumulative += d.precipitation || 0;
      return {
        date: format(new Date(d.date), 'MMM d'),
        rainfall: Math.round((d.precipitation || 0) * 10) / 10,
        cumulative: Math.round(cumulative * 10) / 10,
      };
    });
  }, [historical]);

  // GDD chart data
  const gddChartData = useMemo(() => {
    const data = gddData?.data || [];
    if (!data.length) return [];
    let cumGDD = 0;
    return data.map((d: { date: string; temperatureMax: number; temperatureMin: number }) => {
      const avg = (d.temperatureMax + d.temperatureMin) / 2;
      const dailyGDD = Math.max(0, avg - gddBaseTemp);
      cumGDD += dailyGDD;
      return {
        date: format(new Date(d.date), 'MMM d'),
        dailyGDD: Math.round(dailyGDD * 10) / 10,
        cumulativeGDD: Math.round(cumGDD * 10) / 10,
      };
    });
  }, [gddData, gddBaseTemp]);

  const totalGDD = gddChartData.length > 0 ? gddChartData[gddChartData.length - 1].cumulativeGDD : 0;

  // Frost/heat events
  const frostEvents = useMemo(() => {
    return forecast.filter((d: { temperatureMin: number }) => d.temperatureMin < 4);
  }, [forecast]);

  const heatEvents = useMemo(() => {
    return forecast.filter((d: { temperatureMax: number }) => d.temperatureMax > 35);
  }, [forecast]);

  // Spray conditions from forecast
  const sprayWindows = useMemo(() => {
    return forecast.map((day: { date: string; windSpeed: number; precipitationProbability: number; precipitationSum: number; temperatureMax: number; temperatureMin: number }) => {
      const avgTemp = (day.temperatureMax + day.temperatureMin) / 2;
      const windOk = day.windSpeed < 15;
      const rainOk = day.precipitationProbability < 30 && day.precipitationSum < 2;
      const tempOk = avgTemp >= 15 && avgTemp <= 30;
      return {
        date: day.date,
        suitable: windOk && rainOk && tempOk,
        windOk,
        rainOk,
        tempOk,
        windSpeed: day.windSpeed,
        avgTemp,
      };
    });
  }, [forecast]);

  // Irrigation simple recommendation
  const irrigationAdvice = useMemo(() => {
    if (forecast.length < 3) return null;
    const next3 = forecast.slice(0, 3);
    const rain = next3.reduce((s: number, d: { precipitationSum: number }) => s + (d.precipitationSum || 0), 0);
    const estET = 4 * 3; // ~4mm/day Kenya
    const deficit = estET - rain;
    return {
      forecastRain: Math.round(rain * 10) / 10,
      estET,
      deficit: Math.round(deficit * 10) / 10,
      shouldIrrigate: deficit > 4,
      urgency: deficit > 8 ? 'high' : deficit > 4 ? 'moderate' : 'low',
    };
  }, [forecast]);

  // Total rainfall in 30 days
  const totalRainfall30 = rainfallData.length > 0
    ? rainfallData[rainfallData.length - 1].cumulative
    : 0;

  // Seasonal average for Kenya (~100-150mm/month in wet season, 20-50 in dry)
  const seasonalAvgRainfall = 80; // Rough monthly average

  // ============ RENDER ============

  const condition = currentData?.condition || 'Unknown';
  const gradient = getWeatherGradient(condition);
  const WeatherIcon = getWeatherIcon(condition);

  return (
    <div className="p-6 space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} p-6 lg:p-8 text-white`}>
        <div className="absolute inset-0 bg-black/5" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />

        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold">Weather Dashboard</h1>
            <p className="mt-1 opacity-80">
              {selectedFarm ? `${selectedFarm.name} - ${selectedFarm.cropType || 'Unknown crop'}` : 'Select a farm to view weather'}
            </p>
          </div>

          {/* Farm Selector */}
          <div className="flex items-center gap-3">
            <select
              value={farmId}
              onChange={(e) => setSelectedFarmId(e.target.value)}
              className="px-4 py-2.5 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/50 min-w-[200px]"
            >
              {farms.length === 0 && <option value="">No farms available</option>}
              {farms.map((f) => (
                <option key={f.id} value={f.id} className="text-gray-900">
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick current conditions */}
        {currentData && (
          <div className="relative mt-6 flex flex-wrap items-end gap-6">
            <div className="flex items-center gap-3">
              <WeatherIcon className="h-12 w-12 drop-shadow-lg" />
              <div>
                <div className="text-5xl font-bold leading-none">
                  {Math.round(currentData.temperature)}°
                </div>
                <div className="text-sm opacity-80 mt-1">{condition}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm opacity-90">
              <div>
                <span className="opacity-70">Humidity</span>
                <span className="ml-1 font-semibold">{Math.round(currentData.humidity)}%</span>
              </div>
              <div>
                <span className="opacity-70">Wind</span>
                <span className="ml-1 font-semibold">{Math.round(currentData.windSpeed)} km/h</span>
              </div>
              <div>
                <span className="opacity-70">Precip</span>
                <span className="ml-1 font-semibold">{currentData.precipitation?.toFixed(1) || '0'} mm</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {!farmId ? (
        <Card variant="gradient">
          <div className="text-center py-16 text-gray-500">
            <CloudIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <p className="text-lg font-medium">No Farm Selected</p>
            <p className="mt-1 text-sm">Add a farm to see weather data for its location.</p>
          </div>
        </Card>
      ) : (
        <>
          {/* ============ CURRENT CONDITIONS ============ */}
          {activeTab === 'current' && (
            <CurrentConditionsTab
              currentData={currentData}
              loading={loadingCurrent}
              forecast={forecast}
            />
          )}

          {/* ============ 7-DAY FORECAST ============ */}
          {activeTab === 'forecast' && (
            <ForecastTab
              forecast={forecast}
              loading={loadingForecast}
              expandedDay={expandedDay}
              setExpandedDay={setExpandedDay}
            />
          )}

          {/* ============ RAINFALL ANALYSIS ============ */}
          {activeTab === 'rainfall' && (
            <RainfallTab
              rainfallData={rainfallData}
              loading={loadingHistorical}
              totalRainfall={totalRainfall30}
              seasonalAvg={seasonalAvgRainfall}
            />
          )}

          {/* ============ GDD ============ */}
          {activeTab === 'gdd' && (
            <GDDTab
              gddChartData={gddChartData}
              totalGDD={totalGDD}
              baseTemp={gddBaseTemp}
              setBaseTemp={setGddBaseTemp}
              cropType={selectedFarm?.cropType || null}
              plantingDate={selectedFarm?.plantingDate || null}
            />
          )}

          {/* ============ FROST & HEAT ALERTS ============ */}
          {activeTab === 'alerts' && (
            <AlertsTab
              forecast={forecast}
              frostEvents={frostEvents}
              heatEvents={heatEvents}
              alerts={alerts}
            />
          )}

          {/* ============ ADVISORY ============ */}
          {activeTab === 'advisory' && (
            <AdvisoryTab
              sprayWindows={sprayWindows}
              irrigationAdvice={irrigationAdvice}
              forecast={forecast}
              cropType={selectedFarm?.cropType || null}
            />
          )}
        </>
      )}
    </div>
  );
}

// ============ TAB: CURRENT CONDITIONS ============

function CurrentConditionsTab({
  currentData,
  loading,
  forecast,
}: {
  currentData: Record<string, unknown> | null | undefined;
  loading: boolean;
  forecast: Array<Record<string, unknown>>;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border p-4 animate-pulse">
            <div className="h-3 w-16 bg-gray-200 rounded mb-2" />
            <div className="h-6 w-12 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!currentData) {
    return (
      <Card variant="gradient">
        <p className="text-center text-gray-500 py-8">No current weather data available.</p>
      </Card>
    );
  }

  const temp = currentData.temperature as number ?? 0;
  const humidity = currentData.humidity as number ?? 0;
  const windSpeed = currentData.windSpeed as number ?? 0;
  const precipitation = currentData.precipitation as number ?? 0;
  const condition = currentData.condition as string ?? 'Unknown';

  // Get today's forecast for min/max
  const todayForecast = forecast[0] || {} as Record<string, unknown>;
  const tempMax = todayForecast.temperatureMax as number ?? temp + 3;
  const tempMin = todayForecast.temperatureMin as number ?? temp - 5;

  // Estimate feels like (wind chill simple)
  const feelsLike = temp <= 10 && windSpeed > 5
    ? temp - (windSpeed * 0.3)
    : temp + (humidity > 70 ? 2 : 0);

  const stats = [
    { label: 'Temperature', value: `${Math.round(temp)}°C`, sub: condition },
    { label: 'Feels Like', value: `${Math.round(feelsLike)}°C`, sub: feelsLike < temp ? 'Cooler' : 'Warmer' },
    { label: 'High / Low', value: `${Math.round(tempMax)}° / ${Math.round(tempMin)}°`, sub: 'Today' },
    { label: 'Humidity', value: `${Math.round(humidity)}%`, sub: humidity > 70 ? 'High' : humidity > 40 ? 'Moderate' : 'Low' },
    { label: 'Wind Speed', value: `${Math.round(windSpeed)} km/h`, sub: windSpeed > 30 ? 'Strong' : windSpeed > 15 ? 'Moderate' : 'Light' },
    { label: 'Precipitation', value: `${precipitation.toFixed(1)} mm`, sub: 'Current hour' },
    { label: 'UV Index', value: temp > 25 ? '7 (High)' : temp > 15 ? '4 (Moderate)' : '2 (Low)', sub: 'Estimated' },
    { label: 'Pressure', value: '1013 hPa', sub: 'Standard' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{stat.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Today's forecast mini chart */}
      {forecast.length > 0 && (
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>Next 7 Days Overview</CardTitle>
          </CardHeader>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={forecast.map((d: Record<string, unknown>) => ({
                date: format(new Date(d.date as string), 'EEE'),
                high: Math.round(d.temperatureMax as number),
                low: Math.round(d.temperatureMin as number),
                rain: Math.round((d.precipitationSum as number) || 0),
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} />
                <Bar dataKey="high" name="High °C" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="low" name="Low °C" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="rain" name="Rain mm" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============ TAB: 7-DAY FORECAST ============

function ForecastTab({
  forecast,
  loading,
  expandedDay,
  setExpandedDay,
}: {
  forecast: Array<Record<string, unknown>>;
  loading: boolean;
  expandedDay: string | null;
  setExpandedDay: (d: string | null) => void;
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border p-4 animate-pulse h-20" />
        ))}
      </div>
    );
  }

  if (!forecast.length) {
    return (
      <Card variant="gradient">
        <p className="text-center text-gray-500 py-8">No forecast data available.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {forecast.map((day: Record<string, unknown>) => {
        const date = day.date as string;
        const isExpanded = expandedDay === date;
        const tempMax = day.temperatureMax as number;
        const tempMin = day.temperatureMin as number;
        const precipProb = day.precipitationProbability as number;
        const precipSum = day.precipitationSum as number;
        const wind = day.windSpeed as number;

        const avgTemp = (tempMax + tempMin) / 2;
        const conditionGuess =
          precipProb > 60 ? 'Rainy' :
          precipProb > 30 ? 'Partly Cloudy' :
          avgTemp > 30 ? 'Hot & Clear' : 'Clear';
        const DayIcon = getWeatherIcon(conditionGuess);

        return (
          <div
            key={date}
            className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
          >
            <button
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              onClick={() => setExpandedDay(isExpanded ? null : date)}
            >
              <div className="flex items-center gap-4">
                <DayIcon className="h-8 w-8 text-gray-400" />
                <div className="text-left">
                  <p className="font-semibold text-gray-900">
                    {format(new Date(date), 'EEEE, MMM d')}
                  </p>
                  <p className="text-xs text-gray-500">{conditionGuess}</p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <span className="font-bold text-orange-600">{Math.round(tempMax)}°</span>
                  <span className="text-gray-400 mx-1">/</span>
                  <span className="font-bold text-blue-500">{Math.round(tempMin)}°</span>
                </div>
                <div className="text-right text-sm">
                  <p className="text-blue-500 font-medium">{Math.round(precipProb)}%</p>
                  <p className="text-gray-400 text-xs">{precipSum?.toFixed(1)} mm</p>
                </div>
                <div className="text-right text-sm text-gray-500">
                  {Math.round(wind)} km/h
                </div>
                {isExpanded ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>

            {/* Expanded detail */}
            {isExpanded && (
              <div className="border-t border-gray-100 bg-gray-50 p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Max Temperature</p>
                    <p className="font-semibold">{tempMax.toFixed(1)}°C</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Min Temperature</p>
                    <p className="font-semibold">{tempMin.toFixed(1)}°C</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Avg Temperature</p>
                    <p className="font-semibold">{avgTemp.toFixed(1)}°C</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Temperature Range</p>
                    <p className="font-semibold">{(tempMax - tempMin).toFixed(1)}°C</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Rain Probability</p>
                    <p className="font-semibold">{Math.round(precipProb)}%</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Expected Rain</p>
                    <p className="font-semibold">{precipSum?.toFixed(1)} mm</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Wind Speed</p>
                    <p className="font-semibold">{wind?.toFixed(1)} km/h</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Spray Window</p>
                    <p className={`font-semibold ${wind < 15 && precipProb < 30 && avgTemp >= 15 && avgTemp <= 30 ? 'text-green-600' : 'text-red-500'}`}>
                      {wind < 15 && precipProb < 30 && avgTemp >= 15 && avgTemp <= 30 ? 'Suitable' : 'Not Ideal'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============ TAB: RAINFALL ANALYSIS ============

function RainfallTab({
  rainfallData,
  loading,
  totalRainfall,
  seasonalAvg,
}: {
  rainfallData: { date: string; rainfall: number; cumulative: number }[];
  loading: boolean;
  totalRainfall: number;
  seasonalAvg: number;
}) {
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border p-6 animate-pulse h-80" />
      </div>
    );
  }

  const rainfallPercent = seasonalAvg > 0 ? Math.round((totalRainfall / seasonalAvg) * 100) : 0;
  const droughtRisk =
    rainfallPercent < 40 ? 'High' :
    rainfallPercent < 70 ? 'Moderate' :
    rainfallPercent < 90 ? 'Low' : 'None';
  const droughtColor =
    droughtRisk === 'High' ? 'text-red-600 bg-red-50 border-red-200' :
    droughtRisk === 'Moderate' ? 'text-amber-600 bg-amber-50 border-amber-200' :
    droughtRisk === 'Low' ? 'text-yellow-600 bg-yellow-50 border-yellow-200' :
    'text-green-600 bg-green-50 border-green-200';

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500">Total (30d)</p>
          <p className="text-2xl font-bold text-blue-600">{totalRainfall} mm</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500">Seasonal Avg</p>
          <p className="text-2xl font-bold text-gray-700">{seasonalAvg} mm</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500">% of Average</p>
          <p className={`text-2xl font-bold ${rainfallPercent >= 80 ? 'text-green-600' : rainfallPercent >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
            {rainfallPercent}%
          </p>
        </div>
        <div className={`rounded-xl border p-4 ${droughtColor}`}>
          <p className="text-xs opacity-70">Drought Risk</p>
          <p className="text-2xl font-bold">{droughtRisk}</p>
        </div>
      </div>

      {/* Daily Rainfall Bar Chart */}
      <Card variant="gradient">
        <CardHeader>
          <CardTitle>Daily Rainfall (30 Days)</CardTitle>
        </CardHeader>
        <div className="h-72">
          {rainfallData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rainfallData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval={2} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="rainfall" name="Rainfall (mm)" fill="#3b82f6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">No data</div>
          )}
        </div>
      </Card>

      {/* Cumulative Rainfall Line */}
      <Card variant="gradient">
        <CardHeader>
          <CardTitle>Cumulative Rainfall</CardTitle>
          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
            vs seasonal avg ({seasonalAvg} mm)
          </span>
        </CardHeader>
        <div className="h-64">
          {rainfallData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={rainfallData}>
                <defs>
                  <linearGradient id="cumRainGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval={2} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <ReferenceLine y={seasonalAvg} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Avg', fill: '#ef4444', fontSize: 11 }} />
                <Area type="monotone" dataKey="cumulative" name="Cumulative (mm)" stroke="#3b82f6" fill="url(#cumRainGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">No data</div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ============ TAB: GROWING DEGREE DAYS ============

interface CropStageMarker {
  name: string;
  gdd: number;
}

const CROP_GDD_STAGES: Record<string, CropStageMarker[]> = {
  corn: [
    { name: 'Emergence', gdd: 120 },
    { name: 'V6', gdd: 475 },
    { name: 'Tasseling', gdd: 1135 },
    { name: 'Silking', gdd: 1200 },
    { name: 'Maturity', gdd: 2700 },
  ],
  wheat: [
    { name: 'Emergence', gdd: 100 },
    { name: 'Tillering', gdd: 400 },
    { name: 'Heading', gdd: 1100 },
    { name: 'Maturity', gdd: 1700 },
  ],
  maize: [
    { name: 'Emergence', gdd: 120 },
    { name: 'V6', gdd: 475 },
    { name: 'Tasseling', gdd: 1135 },
    { name: 'Maturity', gdd: 2700 },
  ],
  default: [
    { name: 'Emergence', gdd: 100 },
    { name: 'Growth', gdd: 500 },
    { name: 'Flowering', gdd: 1000 },
    { name: 'Maturity', gdd: 2000 },
  ],
};

function GDDTab({
  gddChartData,
  totalGDD,
  baseTemp,
  setBaseTemp,
  cropType,
  plantingDate,
}: {
  gddChartData: { date: string; dailyGDD: number; cumulativeGDD: number }[];
  totalGDD: number;
  baseTemp: number;
  setBaseTemp: (t: number) => void;
  cropType: string | null;
  plantingDate: string | null;
}) {
  const stages = CROP_GDD_STAGES[(cropType || 'default').toLowerCase()] || CROP_GDD_STAGES.default;
  const currentStage = stages.reduce((current: string, stage) => {
    if (totalGDD >= stage.gdd) return stage.name;
    return current;
  }, 'Pre-emergence');

  const nextStage = stages.find((s) => s.gdd > totalGDD);
  const gddToNext = nextStage ? nextStage.gdd - totalGDD : 0;

  return (
    <div className="space-y-6">
      {/* GDD Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500">Total GDD</p>
          <p className="text-2xl font-bold text-green-600">{totalGDD}</p>
          <p className="text-xs text-gray-400">Base {baseTemp}°C</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500">Current Stage</p>
          <p className="text-lg font-bold text-gray-900">{currentStage}</p>
          <p className="text-xs text-gray-400">{cropType || 'General'}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500">GDD to Next Stage</p>
          <p className="text-2xl font-bold text-blue-600">{nextStage ? Math.round(gddToNext) : '--'}</p>
          <p className="text-xs text-gray-400">{nextStage?.name || 'Complete'}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500">Planting Date</p>
          <p className="text-lg font-bold text-gray-900">
            {plantingDate ? format(new Date(plantingDate), 'MMM d, yyyy') : 'Not set'}
          </p>
        </div>
      </div>

      {/* Base Temperature Selector */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700">Base Temperature:</span>
        {[5, 8, 10, 12, 15].map((t) => (
          <button
            key={t}
            onClick={() => setBaseTemp(t)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              baseTemp === t
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t}°C
          </button>
        ))}
      </div>

      {/* GDD Accumulation Chart */}
      <Card variant="gradient">
        <CardHeader>
          <CardTitle>GDD Accumulation</CardTitle>
        </CardHeader>
        <div className="h-80">
          {gddChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={gddChartData}>
                <defs>
                  <linearGradient id="gddGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval={Math.max(1, Math.floor(gddChartData.length / 10))} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                {/* Crop stage reference lines */}
                {stages.map((stage) => (
                  <ReferenceLine
                    key={stage.name}
                    y={stage.gdd}
                    stroke="#9ca3af"
                    strokeDasharray="3 3"
                    label={{ value: stage.name, fill: '#6b7280', fontSize: 10, position: 'right' }}
                  />
                ))}
                <Area type="monotone" dataKey="cumulativeGDD" name="Cumulative GDD" stroke="#16a34a" fill="url(#gddGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              No GDD data. Set a planting date on your farm to track GDD.
            </div>
          )}
        </div>
      </Card>

      {/* Crop Stage Progress */}
      <Card variant="gradient">
        <CardHeader>
          <CardTitle>Crop Stage Markers ({cropType || 'General'})</CardTitle>
        </CardHeader>
        <div className="space-y-3">
          {stages.map((stage) => {
            const reached = totalGDD >= stage.gdd;
            const progress = Math.min(100, (totalGDD / stage.gdd) * 100);
            return (
              <div key={stage.name} className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${reached ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                  {reached ? <CheckCircleIcon className="h-5 w-5" /> : <span className="text-xs font-bold">{stage.gdd}</span>}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className={`text-sm font-medium ${reached ? 'text-green-700' : 'text-gray-600'}`}>{stage.name}</span>
                    <span className="text-xs text-gray-500">{stage.gdd} GDD</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${reached ? 'bg-green-500' : 'bg-blue-400'}`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ============ TAB: FROST & HEAT ALERTS ============

function AlertsTab({
  forecast,
  frostEvents,
  heatEvents,
  alerts,
}: {
  forecast: Array<Record<string, unknown>>;
  frostEvents: Array<Record<string, unknown>>;
  heatEvents: Array<Record<string, unknown>>;
  alerts: Array<{ type: string; severity: string; message: string; date: string }>;
}) {
  // Temperature extremes chart
  const tempData = forecast.map((d: Record<string, unknown>) => ({
    date: format(new Date(d.date as string), 'EEE'),
    max: Math.round(d.temperatureMax as number),
    min: Math.round(d.temperatureMin as number),
  }));

  return (
    <div className="space-y-6">
      {/* Alert Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`rounded-xl border p-4 ${frostEvents.length > 0 ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            <ExclamationTriangleIcon className={`h-5 w-5 ${frostEvents.length > 0 ? 'text-blue-600' : 'text-green-600'}`} />
            <span className="font-medium text-sm">Frost Risk</span>
          </div>
          <p className={`text-2xl font-bold ${frostEvents.length > 0 ? 'text-blue-700' : 'text-green-700'}`}>
            {frostEvents.length > 0 ? `${frostEvents.length} day(s)` : 'None'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Below 4°C in next 7 days</p>
        </div>

        <div className={`rounded-xl border p-4 ${heatEvents.length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            <ExclamationTriangleIcon className={`h-5 w-5 ${heatEvents.length > 0 ? 'text-red-600' : 'text-green-600'}`} />
            <span className="font-medium text-sm">Heat Stress</span>
          </div>
          <p className={`text-2xl font-bold ${heatEvents.length > 0 ? 'text-red-700' : 'text-green-700'}`}>
            {heatEvents.length > 0 ? `${heatEvents.length} day(s)` : 'None'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Above 35°C in next 7 days</p>
        </div>

        <div className={`rounded-xl border p-4 ${alerts.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            <ExclamationTriangleIcon className={`h-5 w-5 ${alerts.length > 0 ? 'text-amber-600' : 'text-green-600'}`} />
            <span className="font-medium text-sm">Weather Alerts</span>
          </div>
          <p className={`text-2xl font-bold ${alerts.length > 0 ? 'text-amber-700' : 'text-green-700'}`}>
            {alerts.length}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Active alerts</p>
        </div>
      </div>

      {/* Temperature Extremes Chart */}
      <Card variant="gradient">
        <CardHeader>
          <CardTitle>Temperature Extremes (7 Days)</CardTitle>
        </CardHeader>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={tempData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Legend iconType="circle" iconSize={8} />
              <ReferenceLine y={35} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Heat Stress', fill: '#ef4444', fontSize: 10 }} />
              <ReferenceLine y={4} stroke="#3b82f6" strokeDasharray="5 5" label={{ value: 'Frost Risk', fill: '#3b82f6', fontSize: 10 }} />
              <Line type="monotone" dataKey="max" name="Max °C" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="min" name="Min °C" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Alert Details Table */}
      {alerts.length > 0 && (
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>Active Weather Alerts</CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">Date</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">Type</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">Severity</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">Message</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert, idx) => (
                  <tr key={idx} className="border-b border-gray-50">
                    <td className="py-2 px-3">{format(new Date(alert.date), 'MMM d')}</td>
                    <td className="py-2 px-3 capitalize">{alert.type.replace('_', ' ')}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        alert.severity === 'high' ? 'bg-red-100 text-red-700' :
                        alert.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {alert.severity}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-700">{alert.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============ TAB: ADVISORY ============

function AdvisoryTab({
  sprayWindows,
  irrigationAdvice,
  forecast,
  cropType: _cropType,
}: {
  sprayWindows: Array<{
    date: string;
    suitable: boolean;
    windOk: boolean;
    rainOk: boolean;
    tempOk: boolean;
    windSpeed: number;
    avgTemp: number;
  }>;
  irrigationAdvice: { forecastRain: number; estET: number; deficit: number; shouldIrrigate: boolean; urgency: string } | null;
  forecast: Array<Record<string, unknown>>;
  cropType: string | null;
}) {
  // Planting conditions
  const plantingConditions = forecast.map((day: Record<string, unknown>) => {
    const tempMin = day.temperatureMin as number;
    const avgTemp = ((day.temperatureMax as number) + tempMin) / 2;
    const soilTempOk = tempMin >= 8;
    const frostRiskLow = tempMin > 4;
    let score = 50;
    if (soilTempOk) score += 15;
    if (frostRiskLow) score += 20;
    if (avgTemp >= 15 && avgTemp <= 30) score += 10;
    if ((day.windSpeed as number) < 20) score += 5;
    score = Math.max(0, Math.min(100, score));
    return {
      date: day.date as string,
      score,
      suitable: soilTempOk && frostRiskLow && score >= 60,
    };
  });

  // Harvest conditions
  const harvestConditions = forecast.map((day: Record<string, unknown>) => {
    const dryPeriod = (day.precipitationSum as number) < 1 && (day.precipitationProbability as number) < 30;
    const windOk = (day.windSpeed as number) < 30;
    let score = 50;
    if (dryPeriod) score += 25;
    if (windOk) score += 15;
    if ((day.temperatureMax as number) < 35) score += 10;
    score = Math.max(0, Math.min(100, score));
    return {
      date: day.date as string,
      score,
      suitable: dryPeriod && windOk && score >= 65,
    };
  });

  const StatusIcon = ({ ok }: { ok: boolean }) =>
    ok ? <CheckCircleIcon className="h-5 w-5 text-green-500" /> : <XCircleIcon className="h-5 w-5 text-red-400" />;

  return (
    <div className="space-y-6">
      {/* Spraying Conditions */}
      <Card variant="gradient">
        <CardHeader>
          <CardTitle>Spraying Windows</CardTitle>
          <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
            Wind &lt;15 km/h, No rain, 15-30°C
          </span>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 px-3 text-gray-500 font-medium">Date</th>
                <th className="text-center py-2 px-3 text-gray-500 font-medium">Wind</th>
                <th className="text-center py-2 px-3 text-gray-500 font-medium">Rain</th>
                <th className="text-center py-2 px-3 text-gray-500 font-medium">Temp</th>
                <th className="text-center py-2 px-3 text-gray-500 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {sprayWindows.map((day) => (
                <tr key={day.date} className="border-b border-gray-50">
                  <td className="py-2.5 px-3 font-medium">{format(new Date(day.date), 'EEE, MMM d')}</td>
                  <td className="py-2.5 px-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <StatusIcon ok={day.windOk} />
                      <span className="text-xs text-gray-500">{Math.round(day.windSpeed)} km/h</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <StatusIcon ok={day.rainOk} />
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <StatusIcon ok={day.tempOk} />
                      <span className="text-xs text-gray-500">{Math.round(day.avgTemp)}°C</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                      day.suitable ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'
                    }`}>
                      {day.suitable ? 'Good' : 'Poor'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Irrigation Recommendation */}
      {irrigationAdvice && (
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>Irrigation Recommendation</CardTitle>
            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
              irrigationAdvice.urgency === 'high' ? 'bg-red-100 text-red-700' :
              irrigationAdvice.urgency === 'moderate' ? 'bg-amber-100 text-amber-700' :
              'bg-green-100 text-green-700'
            }`}>
              {irrigationAdvice.urgency === 'high' ? 'Irrigate Now' :
               irrigationAdvice.urgency === 'moderate' ? 'Consider Irrigating' :
               'No Action Needed'}
            </span>
          </CardHeader>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-xs text-gray-500">Forecast Rain (3d)</p>
              <p className="text-lg font-bold text-blue-600">{irrigationAdvice.forecastRain} mm</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Est. ET (3d)</p>
              <p className="text-lg font-bold text-orange-600">{irrigationAdvice.estET} mm</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Water Deficit</p>
              <p className={`text-lg font-bold ${irrigationAdvice.deficit > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {irrigationAdvice.deficit > 0 ? `+${irrigationAdvice.deficit}` : irrigationAdvice.deficit} mm
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Action</p>
              <p className="text-lg font-bold text-gray-900">
                {irrigationAdvice.shouldIrrigate ? 'Irrigate' : 'Wait'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Planting & Harvest Conditions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Planting */}
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>Planting Conditions</CardTitle>
          </CardHeader>
          <div className="space-y-2">
            {plantingConditions.map((day: { date: string; score: number; suitable: boolean }) => (
              <div key={day.date} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <span className="text-sm font-medium">{format(new Date(day.date), 'EEE, MMM d')}</span>
                <div className="flex items-center gap-3">
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${day.score >= 70 ? 'bg-green-500' : day.score >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                      style={{ width: `${day.score}%` }}
                    />
                  </div>
                  <span className={`text-xs font-semibold w-10 text-right ${day.suitable ? 'text-green-600' : 'text-gray-400'}`}>
                    {day.score}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Harvest */}
        <Card variant="gradient">
          <CardHeader>
            <CardTitle>Harvest Conditions</CardTitle>
          </CardHeader>
          <div className="space-y-2">
            {harvestConditions.map((day: { date: string; score: number; suitable: boolean }) => (
              <div key={day.date} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <span className="text-sm font-medium">{format(new Date(day.date), 'EEE, MMM d')}</span>
                <div className="flex items-center gap-3">
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${day.score >= 70 ? 'bg-green-500' : day.score >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                      style={{ width: `${day.score}%` }}
                    />
                  </div>
                  <span className={`text-xs font-semibold w-10 text-right ${day.suitable ? 'text-green-600' : 'text-gray-400'}`}>
                    {day.score}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
